import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Layout from '../components/Layout';
import { toast } from 'sonner';
import {
  Search, BookOpen, Bookmark, BookmarkCheck, History, Filter
} from 'lucide-react';
import {
  searchQuranByKeyword,
  searchQuranInArabic,
  getQuranTranslations,
  saveSearchHistory,
  getSearchHistory,
  addQuranBookmark,
  deleteQuranBookmark,
  getQuranBookmarks
} from '../lib/api';

export default function QuranSearch({ user }) {
  const { i18n } = useTranslation();
  const lang = i18n.language;

  const [searchQuery, setSearchQuery] = useState('');
  const [searchMode, setSearchMode] = useState('translation');
  const [selectedTranslation, setSelectedTranslation] = useState('bs.korkut');
  const [translations, setTranslations] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [searchHistory, setSearchHistory] = useState([]);
  const [showSearchHistory, setShowSearchHistory] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [bookmarks, setBookmarks] = useState([]);

  useEffect(() => {
    fetchTranslations();
    fetchSearchHistory();
    fetchBookmarks();
  }, []);

  const fetchTranslations = async () => {
    setTranslations(getQuranTranslations());
  };

  const fetchSearchHistory = async () => {
    try {
      const data = await getSearchHistory(10);
      setSearchHistory(data.filter(h => h.search_type === 'quran'));
    } catch (error) {
      console.error('Error fetching search history:', error);
    }
  };

  const fetchBookmarks = async () => {
    try {
      const data = await getQuranBookmarks();
      setBookmarks(data);
    } catch (error) {
      console.error('Error fetching bookmarks:', error);
    }
  };

  const performSearch = async () => {
    if (!searchQuery.trim()) {
      toast.error(lang === 'hr' ? 'Unesite pojam za pretragu' : 'Enter a search term');
      return;
    }

    setSearching(true);

    try {
      let results;
      if (searchMode === 'arabic') {
        results = await searchQuranInArabic(searchQuery);
      } else {
        results = await searchQuranByKeyword(searchQuery, selectedTranslation);
      }

      setSearchResults(results.matches || []);

      await saveSearchHistory('quran', searchQuery, results.count || 0);
      await fetchSearchHistory();

      if (results.count === 0) {
        toast.info(lang === 'hr' ? 'Nema rezultata' : 'No results found');
      } else {
        toast.success(`${lang === 'hr' ? 'Pronađeno' : 'Found'} ${results.count} ${lang === 'hr' ? 'ajeta' : 'verses'}`);
      }
    } catch (error) {
      console.error('Error searching Quran:', error);
      toast.error(lang === 'hr' ? 'Greška pri pretrazi' : 'Error searching');
    } finally {
      setSearching(false);
    }
  };

  const toggleBookmark = async (surahNum, ayahNum) => {
    const existing = bookmarks.find(b => b.surah_number === surahNum && b.ayah_number === ayahNum);

    if (existing) {
      try {
        await deleteQuranBookmark(existing.id);
        setBookmarks(bookmarks.filter(b => b.id !== existing.id));
        toast.success(lang === 'hr' ? 'Bookmark uklonjen' : 'Bookmark removed');
      } catch (error) {
        console.error('Error removing bookmark:', error);
        toast.error(lang === 'hr' ? 'Greška pri uklanjanju' : 'Error removing bookmark');
      }
    } else {
      try {
        const bookmark = await addQuranBookmark({
          surah_number: surahNum,
          ayah_number: ayahNum,
          page_number: null,
          juz_number: null,
          note: ''
        });
        setBookmarks([...bookmarks, bookmark]);
        toast.success(lang === 'hr' ? 'Bookmark dodan' : 'Bookmark added');
      } catch (error) {
        console.error('Error adding bookmark:', error);
        toast.error(lang === 'hr' ? 'Greška pri dodavanju' : 'Error adding bookmark');
      }
    }
  };

  const isBookmarked = (surahNum, ayahNum) => {
    return bookmarks.some(b => b.surah_number === surahNum && b.ayah_number === ayahNum);
  };

  const highlightText = (text, query) => {
    if (!query.trim()) return text;

    const regex = new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    const parts = text.split(regex);

    return parts.map((part, i) =>
      regex.test(part) ? (
        <mark key={i} className="bg-yellow-200 text-stone-900 px-1 rounded">
          {part}
        </mark>
      ) : part
    );
  };

  return (
    <Layout user={user}>
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-teal-50">
        <div className="max-w-6xl mx-auto p-4 md:p-8">
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
            <div className="bg-gradient-to-r from-emerald-600 to-teal-600 p-6 text-white">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h1 className="text-3xl font-bold flex items-center gap-3">
                    <BookOpen className="w-8 h-8" />
                    {lang === 'hr' ? 'Pretraga Kur\'ana' : 'Quran Search'}
                  </h1>
                  <p className="text-emerald-100 mt-1">
                    {lang === 'hr' ? 'Pretraži Kur\'an po ključnim riječima' : 'Search Quran by keywords'}
                  </p>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setShowFilters(!showFilters)}
                    className="p-3 bg-white/20 hover:bg-white/30 rounded-xl transition-all"
                  >
                    <Filter className="w-6 h-6" />
                  </button>
                  <button
                    onClick={() => setShowSearchHistory(!showSearchHistory)}
                    className="p-3 bg-white/20 hover:bg-white/30 rounded-xl transition-all"
                  >
                    <History className="w-6 h-6" />
                  </button>
                </div>
              </div>

              {showFilters && (
                <div className="mb-6 p-4 bg-white/20 backdrop-blur-sm rounded-xl space-y-4">
                  <div>
                    <label className="block text-sm font-semibold mb-2">
                      {lang === 'hr' ? 'Način pretrage' : 'Search Mode'}
                    </label>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setSearchMode('translation')}
                        className={`px-4 py-2 rounded-lg transition-all ${
                          searchMode === 'translation'
                            ? 'bg-white text-emerald-600 font-semibold'
                            : 'bg-white/20 hover:bg-white/30'
                        }`}
                      >
                        {lang === 'hr' ? 'Prijevod' : 'Translation'}
                      </button>
                      <button
                        onClick={() => setSearchMode('arabic')}
                        className={`px-4 py-2 rounded-lg transition-all ${
                          searchMode === 'arabic'
                            ? 'bg-white text-emerald-600 font-semibold'
                            : 'bg-white/20 hover:bg-white/30'
                        }`}
                      >
                        {lang === 'hr' ? 'Arapski' : 'Arabic'}
                      </button>
                    </div>
                  </div>

                  {searchMode === 'translation' && (
                    <div>
                      <label className="block text-sm font-semibold mb-2">
                        {lang === 'hr' ? 'Odaberi prijevod' : 'Select Translation'}
                      </label>
                      <select
                        value={selectedTranslation}
                        onChange={(e) => setSelectedTranslation(e.target.value)}
                        className="w-full px-4 py-2 bg-white/30 border border-white/40 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-white/50"
                      >
                        {translations.map((trans) => (
                          <option key={trans.identifier} value={trans.identifier} className="text-stone-800">
                            {trans.name} ({trans.language})
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>
              )}

              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-emerald-300" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && performSearch()}
                    placeholder={
                      searchMode === 'arabic'
                        ? (lang === 'hr' ? 'Pretraži arapski tekst...' : 'Search Arabic text...')
                        : (lang === 'hr' ? 'Pretraži prijevod...' : 'Search translation...')
                    }
                    className="w-full pl-10 pr-4 py-3 bg-white/20 backdrop-blur-sm border border-white/30 rounded-xl text-white placeholder-emerald-200 focus:outline-none focus:ring-2 focus:ring-white/50"
                  />
                </div>
                <button
                  onClick={performSearch}
                  disabled={searching}
                  className="px-6 py-3 bg-white text-emerald-600 rounded-xl hover:bg-emerald-50 transition-all font-medium disabled:opacity-50"
                >
                  {searching ? (lang === 'hr' ? 'Pretraživanje...' : 'Searching...') : (lang === 'hr' ? 'Pretraži' : 'Search')}
                </button>
              </div>

              {showSearchHistory && searchHistory.length > 0 && (
                <div className="mt-4 bg-white/20 backdrop-blur-sm rounded-xl p-4">
                  <h3 className="text-sm font-semibold mb-2">{lang === 'hr' ? 'Nedavne pretrage' : 'Recent Searches'}</h3>
                  <div className="flex flex-wrap gap-2">
                    {searchHistory.map((item, idx) => (
                      <button
                        key={idx}
                        onClick={() => {
                          setSearchQuery(item.query);
                          performSearch();
                        }}
                        className="px-3 py-1 bg-white/20 hover:bg-white/30 rounded-lg text-sm transition-all"
                      >
                        {item.query} ({item.results_count})
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="p-6">
              {searching ? (
                <div className="flex items-center justify-center py-20">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-4 border-emerald-600 border-t-transparent mx-auto mb-4"></div>
                    <p className="text-stone-600">{lang === 'hr' ? 'Pretraživanje...' : 'Searching...'}</p>
                  </div>
                </div>
              ) : searchResults.length > 0 ? (
                <div className="space-y-4">
                  <h2 className="text-xl font-bold text-stone-800 mb-4">
                    {lang === 'hr' ? 'Rezultati pretrage' : 'Search Results'} ({searchResults.length})
                  </h2>

                  {searchResults.map((result, idx) => (
                    <div key={idx} className="border border-stone-200 rounded-xl p-6 hover:shadow-md transition-all">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <h3 className="font-semibold text-emerald-600 mb-1">
                            {result.surah.englishName} ({result.surah.name})
                          </h3>
                          <p className="text-sm text-stone-500">
                            {lang === 'hr' ? 'Ajet' : 'Verse'} {result.numberInSurah} - {result.surah.englishNameTranslation}
                          </p>
                        </div>
                        <button
                          onClick={() => toggleBookmark(result.surah.number, result.numberInSurah)}
                          className={`p-2 rounded-lg transition-all ${
                            isBookmarked(result.surah.number, result.numberInSurah)
                              ? 'bg-amber-600 text-white'
                              : 'bg-amber-100 text-amber-700 hover:bg-amber-200'
                          }`}
                        >
                          {isBookmarked(result.surah.number, result.numberInSurah) ? (
                            <BookmarkCheck className="w-5 h-5" />
                          ) : (
                            <Bookmark className="w-5 h-5" />
                          )}
                        </button>
                      </div>

                      <div className="bg-stone-50 rounded-lg p-4">
                        <p className="text-base text-stone-700 leading-relaxed">
                          {searchQuery ? highlightText(result.text, searchQuery) : result.text}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-20">
                  <Search className="w-16 h-16 text-stone-300 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-stone-700 mb-2">
                    {lang === 'hr' ? 'Pretraži Kur\'an' : 'Search the Quran'}
                  </h3>
                  <p className="text-stone-500 max-w-md mx-auto">
                    {lang === 'hr'
                      ? 'Unesite ključne riječi ili frazu da pronađete ajete koji sadrže taj tekst.'
                      : 'Enter keywords or phrases to find verses containing that text.'}
                  </p>
                  <div className="mt-6 space-y-2 text-sm text-stone-600 max-w-lg mx-auto">
                    <p>
                      <span className="font-semibold">{lang === 'hr' ? 'Savjet:' : 'Tip:'}</span>{' '}
                      {lang === 'hr'
                        ? 'Možete pretraživati po prijevodima (engleski, bosanski) ili direktno po arapskom tekstu.'
                        : 'You can search by translations (English, Bosnian) or directly in Arabic text.'}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
